// @flow
import * as React from 'react';
import { Box } from 'grommet';
import styled from 'styled-components';
import GenericApiForm from 'speed-js-react-web/src/containers/grommet2/forms/ApiForm';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';

type Props = {
    onClose: Function,
    handleSubmitAndNew: Function,
    height: string,
    actions?: React.Node,
    forwardedRef: React.Ref,
    padding?: string | Object,
    setIsSubmitting: Function,
    isDisabled: Boolean,
    hasActions?: boolean,
    overflow: String,
    validateOnBlur?: boolean,
    validateOnChange?: boolean,
}

const StyledFormBox = styled(Box)`
    display: block;
    height: 100%;
`;

const StyledActionBox = styled(Box)`
    width: 100%;
    height: 52px;
    position: absolute;
    background-color: #ffffff;
    bottom: 0;
    left: 0;
    box-shadow: ${(props) => (props.shadow ? '0px 0px 10px #c0bfbf' : 'none')};
`;

const useGetShadowBottom = () => {
    const [shadow, setShadow] = React.useState(false);
    // If the content overflows enables shadow on the bottom
    const getShadowBottom = React.useCallback((e) => {
        if (e) {
            if (e.target.className && e.target.className.includes('select__menu-list')) {
                return;
            }
            const { scrollTop, scrollHeight, offsetHeight } = e.target;
            const differenceScroll = Math.floor(scrollHeight - offsetHeight);
            let shadowBottom = false;
            if (Math.floor(scrollTop) !== differenceScroll
                && Math.ceil(scrollTop) !== differenceScroll) {
                shadowBottom = true;
            }
            setShadow(shadowBottom);
        }
    }, []);

    // A delay in executing getShadows is necessary when resize event is fired.Otherwise it doesn't work properly.
    // Try to fix it if you can
    const handleResize = React.useCallback(() => {
        setTimeout(() => getShadowBottom(), 50);
    }, [getShadowBottom]);

    // Add/Remove event listener
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);


    return [shadow, getShadowBottom];
};

const ApiForm: React.Node = (props: Props) => {
    const [grommetFormBoxRef, setGrommetFormBoxRef] = React.useState(null);
    const boxRef = React.useRef();

    const [shadow, getShadowBottom] = useGetShadowBottom();

    // See if shadow bottom is enabled when components mounts for the first time and when the form updates.
    React.useEffect(() => {
        getShadowBottom();
    }, [grommetFormBoxRef, getShadowBottom]);

    const {
        onClose,
        actions,
        hasActions,
        handleSubmitAndNew,
    } = props;
    const renderActions = React.useCallback(({ isSubmitting }: { isSubmitting: boolean }, isDisabled = false) => (
        <StyledActionBox shadow={shadow}>
            <Spinner loading={isSubmitting} />
            {actions || (
                <ActionButtons
                    disabled={isSubmitting || isDisabled}
                    onClose={onClose}
                    onSaveAndContinue={handleSubmitAndNew}
                />
            )}
        </StyledActionBox>
    ), [actions, onClose, shadow]);

    const {
        height, forwardedRef, padding, overflow,
    } = props;

    return (
        <StyledFormBox ref={boxRef} overflow={overflow || 'auto'} onScroll={getShadowBottom}>
            <Box pad={padding} height={height}>
                <GenericApiForm
                    {...props}
                    actions={hasActions || renderActions}
                    ref={forwardedRef}
                    getGrommetFormBoxRef={setGrommetFormBoxRef}
                />
            </Box>
        </StyledFormBox>
    );
};

ApiForm.defaultProps = {
    height: null,
    actions: null,
    hasActions: false,
    padding: { bottom: '60px', horizontal: 'small' },
    validateOnBlur: true,
    validateOnChange: true,
};

export { StyledFormBox, StyledActionBox, useGetShadowBottom };

export default React.forwardRef((props, ref) => (
    <ApiForm forwardedRef={ref} {...props} />
));
